import React from "react";
import "./Strategies.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faScaleBalanced,
  faEye,
  faHandshake,
  faLink,
  faCookieBite,
  faTriangleExclamation,
  faGlobe,
  faShieldHalved,
  faComments,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

const strategyItems = [
  {
    icon: faScaleBalanced,
    pill: "Fairness",
    title: "Honest comparisons",
    description:
      "When we compare products or services, we explain criteria and limits. Rankings are not paid placement unless labeled sponsored.",
  },
  {
    icon: faEye,
    pill: "Clarity",
    title: "Visible business identity",
    description:
      "Who we are, how to contact us, and where to read policies stay easy to find—important for advertising and consumer trust.",
  },
  {
    icon: faHandshake,
    pill: "Brand respect",
    title: "Trademarks & platforms",
    description:
      "Third-party logos belong to their owners. We use them accurately and never imply endorsement we do not have.",
  },
  {
    icon: faLink,
    pill: "Disclosure",
    title: "Links & compensation",
    description:
      "Affiliate, referral, or sponsored links are disclosed in line with FTC guidance. Ads are labeled as advertising.",
  },
  {
    icon: faCookieBite,
    pill: "Privacy",
    title: "Data minimization",
    description:
      "Cookies and analytics are described in our privacy policy. Forms collect only what we need to respond to you.",
  },
  {
    icon: faTriangleExclamation,
    pill: "Safety",
    title: "Risk-aware guidance",
    description:
      "We flag sensitive verticals (health, finance, kids) where extra claims review or licensing may apply.",
  },
  {
    icon: faGlobe,
    pill: "Locale",
    title: "Markets & locale",
    description:
      "Tone, creative, and compliance notes adapt to your primary markets—including regional privacy and pricing rules.",
  },
  {
    icon: faShieldHalved,
    pill: "Security",
    title: "Account security",
    description:
      "Least-privilege access, 2FA, and documented admin roles. We never ask for unrelated passwords.",
  },
  {
    icon: faComments,
    pill: "Feedback",
    title: "Client communication",
    description:
      "Decisions are documented in writing. We welcome pushback on risky claims and adjust when facts change.",
  },
];

const Strategies = () => {
  return (
    <section className="strat" aria-labelledby="strat-heading">
      <div className="strat__wrap">
        <motion.div
          className="strat__head"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.65 }}
        >
          <p className="strat__eyebrow">Compliance-minded marketing</p>
          <h2 id="strat-heading" className="strat__title">
            Principles for clients and ad platforms
          </h2>
          <p className="strat__sub">
            These pillars summarize how we align with honest advertising norms. They are not legal advice—consult
            counsel for regulated industries or complex campaigns. Destination pages should match ad promises, load
            over HTTPS, and keep privacy disclosures accurate.
          </p>
        </motion.div>

        <motion.div
          className="strat__grid"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
        >
          {strategyItems.map((entry) => (
            <motion.article className="strat__card" key={entry.title} variants={item}>
              <div className="strat__top">
                <div className="strat__icon" aria-hidden="true">
                  <FontAwesomeIcon icon={entry.icon} />
                </div>
                <span className="strat__pill">{entry.pill}</span>
              </div>
              <h3 className="strat__h3">{entry.title}</h3>
              <p className="strat__p">{entry.description}</p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Strategies;
